import {
  Badge,
  Banner,
  Button,
  Checkbox,
  Empty,
  Input,
  InputArea,
  Loader,
  Select,
  Surface,
  Switch,
  Table,
  Tabs,
  Text,
} from '@cloudflare/kumo'
import { ArrowsClockwise, DownloadSimple, Megaphone, Plus } from '@phosphor-icons/react'
import {
  AD_BILLING_UNITS,
  AD_LEAD_ITEMS,
  AD_OFFER_KINDS,
  AD_PLACEMENTS,
  adAdvertiserInputSchema,
  adCampaignInputSchema,
  adCopyProblems,
  COMMON_INGREDIENT_KEYS,
  COMMON_INGREDIENTS,
  MARKETS,
  PRICE_BASE_UNITS,
  TAXONOMY,
  type AdAdvertiserInput,
  type AdAdvertiserView,
  type AdBillingUnit,
  type AdCampaignAdminView,
  type AdCampaignInput,
  type AdCopyProblem,
  type AdLeadAdminView,
  type AdLeadItem,
  type AdOfferKind,
  type AdPlacement,
  type AdStatus,
} from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { adsApi, type AdStatusTarget } from '../ads-api'
import { PageHeader } from '../components/page-header'
import { useGetToken } from '../lib/auth'
import { fmtInt, fmtTime, toMessage } from '../lib/format'

const PLACEMENT_LABEL: Record<AdPlacement, string> = {
  ingredient_swap: '원료 대체 (재료 줄)',
  packaging: '포장재 (원가 포장 줄)',
  factory: '추천 공장 (견적 비교)',
  generation_wait: '생성 대기',
  menu_supply: '업소용 받는 곳 (메뉴 재료 줄)',
}
const OFFER_LABEL: Record<AdOfferKind, string> = {
  ingredient: '원료·식재료',
  packaging: '포장재',
  factory: '공장',
  food_distribution: '식자재 유통',
  kitchen_equipment: '주방 설비·기기',
  delivery_packaging: '배달 용기',
  pos: 'POS·주문 앱',
  menu_design: '메뉴판·매장 디자인',
}
const BILLING_LABEL: Record<AdBillingUnit, string> = {
  per_lead: '문의 1건당',
  per_click: '클릭당',
  per_mille: '노출 1,000회당',
  fixed_slot: '월 고정 슬롯',
}
const STATUS_LABEL: Record<AdStatus, string> = {
  draft: '임시 저장',
  pending_review: '검수 대기',
  approved: '집행 중',
  paused: '일시 중지',
  rejected: '반려',
}
const LEAD_ITEM_LABEL: Record<AdLeadItem, string> = {
  company: '회사명',
  contact: '연락처',
  usage: '예상 사용량',
  store: '가게 이름',
  region: '지역',
}
const PROBLEM_LABEL: Record<AdCopyProblem['code'], string> = {
  health_claim: '효능·질병 표현',
  contract_unconfirmed: '광고주 계약 미확인 (실명 노출 금지)',
  price_missing: '단가·단위 없음',
  ingredient_key_missing: '공통 식재료 종류 없음',
  lead_items_missing: '문의 과금인데 넘길 항목 없음',
}

const problemText = (p: AdCopyProblem) =>
  p.code === 'health_claim'
    ? `${PROBLEM_LABEL[p.code]}: ${p.matches.join(', ')}`
    : PROBLEM_LABEL[p.code]

const today = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10)

function emptyCampaign(advertiserId: string): AdCampaignInput {
  return {
    advertiserId,
    placement: 'ingredient_swap',
    offerKind: 'ingredient',
    market: 'kr',
    axis: null,
    majorIds: [],
    ingredientKey: null,
    content: {
      productName: '',
      body: '',
      specText: '',
      priceAmount: null,
      priceUnit: null,
      minOrderText: '',
      sampleText: '',
      deliveryText: '',
      labelText: '',
      originText: '',
      tasteNote: '',
    },
    leadItems: ['company', 'contact', 'usage'],
    billingUnit: 'per_lead',
    billingAmount: 0,
    monthlyBudget: 0,
    startsOn: today(),
    endsOn: null,
  }
}

const EMPTY_ADVERTISER: AdAdvertiserInput = {
  name: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  contractConfirmed: false,
  memo: '',
}

const items = <T extends string>(values: readonly T[], label: (v: T) => string) =>
  values.map((value) => ({ value, label: label(value) }))

/**
 * 광고 관리 (B2B 광고, 대표 결정 4). 광고주·광고 카드 등록, 문구 검사 뒤 승인, 이번 달 성과, 문의 목록과 내보내기.
 * 실존 회사는 계약을 확인한 광고주만 승인된다. '광고' 표시는 앱이 늘 붙이고 여기서 끌 수 없다.
 * 앱에 보일지는 [설정 › B2B 광고] 스위치가 따로 정한다
 */
export function AdsPage() {
  const getToken = useGetToken()
  const [tab, setTab] = useState<'campaigns' | 'advertisers' | 'leads'>('campaigns')
  const [advertisers, setAdvertisers] = useState<AdAdvertiserView[] | null>(null)
  const [campaigns, setCampaigns] = useState<AdCampaignAdminView[]>([])
  const [leads, setLeads] = useState<AdLeadAdminView[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<{ id: string | null; input: AdCampaignInput } | null>(null)
  const [advertiserForm, setAdvertiserForm] = useState<{
    id: string | null
    input: AdAdvertiserInput
  } | null>(null)

  const run = useCallback(async (work: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await work()
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }, [])

  const reload = useCallback(
    () =>
      run(async () => {
        const [overview, leadList] = await Promise.all([
          adsApi.overview(getToken),
          adsApi.leads(getToken),
        ])
        setAdvertisers(overview.advertisers)
        setCampaigns(overview.campaigns)
        setLeads(leadList.items)
      }),
    [getToken, run],
  )

  useEffect(() => {
    void reload()
  }, [reload])

  const advertiserName = (id: string) => advertisers?.find((a) => a.id === id)?.name ?? '—'

  const saveCampaign = () =>
    editing &&
    run(async () => {
      const input = adCampaignInputSchema.parse(editing.input)
      if (editing.id) await adsApi.updateCampaign(getToken, editing.id, input)
      else await adsApi.createCampaign(getToken, input)
      setEditing(null)
      await reload()
    })

  const setStatus = (id: string, status: AdStatusTarget) =>
    run(async () => {
      await adsApi.setStatus(getToken, id, status)
      await reload()
    })

  const saveAdvertiser = () =>
    advertiserForm &&
    run(async () => {
      const input = adAdvertiserInputSchema.parse(advertiserForm.input)
      if (advertiserForm.id) await adsApi.updateAdvertiser(getToken, advertiserForm.id, input)
      else await adsApi.createAdvertiser(getToken, input)
      setAdvertiserForm(null)
      await reload()
    })

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="광고 관리"
        description="원료사·포장재사·공장·식자재 유통사의 B2B 광고. 회사는 광고 계약을 확인한 뒤에만 승인돼요. 앱 노출은 [설정 › B2B 광고]가 켜져 있을 때만이에요."
        actions={
          <Button
            variant="secondary"
            icon={<ArrowsClockwise />}
            loading={busy}
            onClick={() => void reload()}
          >
            새로고침
          </Button>
        }
      />
      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}

      <Tabs
        variant="underline"
        size="sm"
        value={tab}
        onValueChange={(v) => setTab(v as typeof tab)}
        tabs={[
          { value: 'campaigns', label: `광고 카드 ${campaigns.length}` },
          { value: 'advertisers', label: `광고주 ${advertisers?.length ?? 0}` },
          { value: 'leads', label: `문의 ${leads.length}` },
        ]}
      />

      {advertisers === null ? (
        <div className="flex items-center gap-3 p-6">
          <Loader />
          <Text variant="secondary">불러오는 중…</Text>
        </div>
      ) : tab === 'campaigns' ? (
        <>
          <div>
            <Button
              icon={<Plus />}
              disabled={advertisers.length === 0}
              onClick={() => setEditing({ id: null, input: emptyCampaign(advertisers[0]!.id) })}
            >
              광고 카드 만들기
            </Button>
            {advertisers.length === 0 && (
              <Text variant="secondary" size="sm">
                먼저 [광고주] 탭에서 광고주를 등록하세요.
              </Text>
            )}
          </div>
          {editing && (
            <CampaignEditor
              value={editing.input}
              advertisers={advertisers}
              busy={busy}
              onChange={(input) => setEditing({ ...editing, input })}
              onSave={() => void saveCampaign()}
              onCancel={() => setEditing(null)}
            />
          )}
          <CampaignTable
            campaigns={campaigns}
            advertiserName={advertiserName}
            onEdit={(c) => setEditing({ id: c.id, input: adCampaignInputSchema.parse(c) })}
            onStatus={(id, status) => void setStatus(id, status)}
            onExport={(id) => void run(() => adsApi.downloadLeads(getToken, id))}
          />
        </>
      ) : tab === 'advertisers' ? (
        <>
          <div>
            <Button
              icon={<Plus />}
              onClick={() => setAdvertiserForm({ id: null, input: EMPTY_ADVERTISER })}
            >
              광고주 등록
            </Button>
          </div>
          {advertiserForm && (
            <Surface className="flex flex-col gap-4 rounded-lg p-6">
              <Input
                label="회사 이름 (앱 광고 카드에 그대로 보여요)"
                value={advertiserForm.input.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAdvertiserForm({
                    ...advertiserForm,
                    input: { ...advertiserForm.input, name: e.target.value },
                  })
                }
              />
              <div className="flex flex-wrap gap-3">
                {(['contactName', 'contactEmail', 'contactPhone'] as const).map((key) => (
                  <Input
                    key={key}
                    label={
                      { contactName: '담당자', contactEmail: '이메일', contactPhone: '전화' }[key]
                    }
                    value={advertiserForm.input[key]}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setAdvertiserForm({
                        ...advertiserForm,
                        input: { ...advertiserForm.input, [key]: e.target.value },
                      })
                    }
                  />
                ))}
              </div>
              <Switch
                label="광고 계약 확인 (회사명·상표 표기 허락 포함). 끄면 이 회사 광고는 승인할 수 없어요"
                checked={advertiserForm.input.contractConfirmed}
                onCheckedChange={(contractConfirmed: boolean) =>
                  setAdvertiserForm({
                    ...advertiserForm,
                    input: { ...advertiserForm.input, contractConfirmed },
                  })
                }
              />
              <InputArea
                label="메모"
                required={false}
                value={advertiserForm.input.memo}
                onValueChange={(memo: string) =>
                  setAdvertiserForm({ ...advertiserForm, input: { ...advertiserForm.input, memo } })
                }
              />
              <div className="flex gap-2">
                <Button
                  loading={busy}
                  disabled={!adAdvertiserInputSchema.safeParse(advertiserForm.input).success}
                  onClick={() => void saveAdvertiser()}
                >
                  저장
                </Button>
                <Button variant="secondary" onClick={() => setAdvertiserForm(null)}>
                  닫기
                </Button>
              </div>
            </Surface>
          )}
          <Surface className="overflow-x-auto rounded-lg p-0">
            {advertisers.length === 0 ? (
              <Empty
                icon={<Megaphone size={40} className="text-kumo-inactive" />}
                title="광고주가 없어요"
                description="계약 전 회사는 등록만 하고 계약 확인을 켜지 마세요."
              />
            ) : (
              <Table>
                <Table.Header variant="compact">
                  <Table.Row>
                    <Table.Head>회사</Table.Head>
                    <Table.Head>담당자</Table.Head>
                    <Table.Head>계약</Table.Head>
                    <Table.Head />
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {advertisers.map((a) => (
                    <Table.Row key={a.id}>
                      <Table.Cell>{a.name}</Table.Cell>
                      <Table.Cell className="text-xs">
                        {[a.contactName, a.contactEmail, a.contactPhone]
                          .filter(Boolean)
                          .join(' · ')}
                      </Table.Cell>
                      <Table.Cell>
                        {a.contractConfirmed ? (
                          <Badge variant="primary">확인</Badge>
                        ) : (
                          <Badge variant="secondary">미확인</Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setAdvertiserForm({ id: a.id, input: a })}
                        >
                          편집
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </Surface>
        </>
      ) : (
        <LeadTable
          leads={leads}
          campaigns={campaigns}
          advertiserName={advertiserName}
          busy={busy}
          onExport={() => void run(() => adsApi.downloadLeads(getToken))}
        />
      )}
    </div>
  )
}

function CampaignTable({
  campaigns,
  advertiserName,
  onEdit,
  onStatus,
  onExport,
}: {
  campaigns: AdCampaignAdminView[]
  advertiserName: (id: string) => string
  onEdit: (c: AdCampaignAdminView) => void
  onStatus: (id: string, status: AdStatusTarget) => void
  onExport: (id: string) => void
}) {
  if (campaigns.length === 0)
    return (
      <Empty
        icon={<Megaphone size={40} className="text-kumo-inactive" />}
        title="광고 카드가 없어요"
        description="광고주가 없으면 앱에는 광고 칸 자체가 그려지지 않아요."
      />
    )
  return (
    <Surface className="overflow-x-auto rounded-lg p-0">
      <Table>
        <Table.Header variant="compact">
          <Table.Row>
            <Table.Head>상태</Table.Head>
            <Table.Head>광고주 · 원료</Table.Head>
            <Table.Head>자리</Table.Head>
            <Table.Head>대상</Table.Head>
            <Table.Head>기간</Table.Head>
            <Table.Head>과금 · 예산</Table.Head>
            <Table.Head>이번 달</Table.Head>
            <Table.Head>문구 검사</Table.Head>
            <Table.Head />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {campaigns.map((c) => (
            <Table.Row key={c.id}>
              <Table.Cell>
                <Badge variant={c.status === 'approved' ? 'primary' : 'secondary'}>
                  {STATUS_LABEL[c.status]}
                </Badge>
              </Table.Cell>
              <Table.Cell className="text-xs">
                <div>{advertiserName(c.advertiserId)}</div>
                <div className="text-kumo-subtle">{c.content.productName}</div>
              </Table.Cell>
              <Table.Cell className="text-xs">{PLACEMENT_LABEL[c.placement]}</Table.Cell>
              <Table.Cell className="text-xs">
                <div>
                  {c.market.toUpperCase()} ·{' '}
                  {c.axis === 'menu' ? '메뉴' : c.axis === 'product' ? '식품' : '식품·메뉴'}
                </div>
                <div className="text-kumo-subtle">
                  {c.ingredientKey
                    ? COMMON_INGREDIENTS[c.ingredientKey].label.ko
                    : OFFER_LABEL[c.offerKind]}
                  {c.majorIds.length ? ` · ${c.majorIds.join(', ')}` : ' · 전체 분류'}
                </div>
              </Table.Cell>
              <Table.Cell className="whitespace-nowrap text-xs">
                {c.startsOn}~{c.endsOn ?? ''}
              </Table.Cell>
              <Table.Cell className="whitespace-nowrap text-xs">
                <div>
                  {BILLING_LABEL[c.billingUnit]} {fmtInt(c.billingAmount)}
                </div>
                <div className="text-kumo-subtle">
                  {c.billingUnit === 'fixed_slot'
                    ? '고정'
                    : `${fmtInt(Math.round(c.month.spend))} / ${fmtInt(c.monthlyBudget)}`}
                </div>
              </Table.Cell>
              <Table.Cell className="whitespace-nowrap text-xs">
                노출 {fmtInt(c.month.impressions)} · 클릭 {fmtInt(c.month.clicks)} · 문의{' '}
                {fmtInt(c.month.leads)}
              </Table.Cell>
              <Table.Cell className="text-xs">
                {c.problems.length === 0 ? (
                  <Badge variant="primary">통과</Badge>
                ) : (
                  c.problems.map((p) => <div key={p.code}>{problemText(p)}</div>)
                )}
              </Table.Cell>
              <Table.Cell>
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="secondary" onClick={() => onEdit(c)}>
                    편집
                  </Button>
                  {c.status === 'draft' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onStatus(c.id, 'pending_review')}
                    >
                      검수 요청
                    </Button>
                  )}
                  {c.status !== 'approved' && (
                    <Button
                      size="sm"
                      disabled={c.problems.length > 0}
                      onClick={() => onStatus(c.id, 'approved')}
                    >
                      승인하고 게시
                    </Button>
                  )}
                  {c.status === 'approved' && (
                    <Button size="sm" variant="secondary" onClick={() => onStatus(c.id, 'paused')}>
                      일시 중지
                    </Button>
                  )}
                  {c.status === 'pending_review' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onStatus(c.id, 'rejected')}
                    >
                      반려
                    </Button>
                  )}
                  {c.leadItems.length > 0 && (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<DownloadSimple />}
                      onClick={() => onExport(c.id)}
                    >
                      문의 CSV
                    </Button>
                  )}
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Surface>
  )
}

function CampaignEditor({
  value,
  advertisers,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  value: AdCampaignInput
  advertisers: AdAdvertiserView[]
  busy: boolean
  onChange: (v: AdCampaignInput) => void
  onSave: () => void
  onCancel: () => void
}) {
  const set = <K extends keyof AdCampaignInput>(key: K, v: AdCampaignInput[K]) =>
    onChange({ ...value, [key]: v })
  const setContent = <K extends keyof AdCampaignInput['content']>(
    key: K,
    v: AdCampaignInput['content'][K],
  ) => onChange({ ...value, content: { ...value.content, [key]: v } })
  const parsed = adCampaignInputSchema.safeParse(value)
  const advertiser = advertisers.find((a) => a.id === value.advertiserId)
  const problems = parsed.success
    ? adCopyProblems(parsed.data, { contractConfirmed: advertiser?.contractConfirmed ?? false })
    : []
  const majors = value.axis ? TAXONOMY[value.axis] : [...TAXONOMY.product, ...TAXONOMY.menu]
  const majorItems = [...new Map(majors.map((m) => [m.id, m.ko])).entries()]
  const num = (s: string) => (s.trim() === '' ? null : Number(s))
  const textInput = (key: keyof AdCampaignInput['content'], label: string) => (
    <Input
      label={label}
      value={String(value.content[key] ?? '')}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        setContent(key, e.target.value as never)
      }
    />
  )

  return (
    <Surface className="flex flex-col gap-5 rounded-lg p-6">
      <Text variant="heading">광고 카드 편집</Text>
      <div className="flex flex-wrap gap-3">
        <Select
          label="광고주"
          value={value.advertiserId}
          onValueChange={(v: string | null) => v && set('advertiserId', v)}
          items={advertisers.map((a) => ({ value: a.id, label: a.name }))}
        />
        <Select
          label="자리"
          value={value.placement}
          onValueChange={(v: string | null) => v && set('placement', v as AdPlacement)}
          items={items(AD_PLACEMENTS, (p) => PLACEMENT_LABEL[p])}
        />
        <Select
          label="종류"
          value={value.offerKind}
          onValueChange={(v: string | null) => v && set('offerKind', v as AdOfferKind)}
          items={items(AD_OFFER_KINDS, (k) => OFFER_LABEL[k])}
        />
        <Select
          label="시장"
          value={value.market}
          onValueChange={(v: string | null) => v && set('market', v as AdCampaignInput['market'])}
          items={items(MARKETS, (m) => (m === 'kr' ? '한국' : '미국'))}
        />
        <Select
          label="축"
          value={value.axis ?? 'all'}
          onValueChange={(v: string | null) =>
            onChange({ ...value, axis: v === 'product' || v === 'menu' ? v : null, majorIds: [] })
          }
          items={[
            { value: 'all', label: '식품·메뉴 모두' },
            { value: 'product', label: '식품' },
            { value: 'menu', label: '메뉴' },
          ]}
        />
        <Select
          label="공통 식재료 (원료 대체·업소용 받는 곳)"
          value={value.ingredientKey ?? 'none'}
          onValueChange={(v: string | null) =>
            set('ingredientKey', v && v !== 'none' ? (v as AdCampaignInput['ingredientKey']) : null)
          }
          items={[
            { value: 'none', label: '없음' },
            ...items(COMMON_INGREDIENT_KEYS, (k) => `${COMMON_INGREDIENTS[k].label.ko} (${k})`),
          ]}
        />
      </div>

      <Checkbox.Group
        legend="노출 대분류 (비우면 전체)"
        value={value.majorIds}
        onValueChange={(majorIds: string[]) => set('majorIds', majorIds)}
        className="flex flex-wrap gap-x-4 gap-y-1"
      >
        {majorItems.map(([id, label]) => (
          <Checkbox.Item key={id} value={id} label={label} />
        ))}
      </Checkbox.Group>

      <div className="flex flex-wrap gap-3">
        {textInput('productName', '광고 원료·서비스 이름')}
        {textInput('specText', '업소용 규격')}
        <Input
          label="단가 (부가세 별도)"
          type="number"
          value={value.content.priceAmount === null ? '' : String(value.content.priceAmount)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setContent('priceAmount', num(e.target.value))
          }
        />
        <Select
          label="단가 기준"
          value={value.content.priceUnit ?? 'none'}
          onValueChange={(v: string | null) =>
            setContent(
              'priceUnit',
              v && v !== 'none' ? (v as (typeof PRICE_BASE_UNITS)[number]) : null,
            )
          }
          items={[{ value: 'none', label: '없음' }, ...items(PRICE_BASE_UNITS, (u) => `${u} 당`)]}
        />
        {textInput('minOrderText', '최소 주문')}
        {textInput('sampleText', '샘플')}
        {textInput('deliveryText', '배송')}
        {textInput('originText', '원산지 표기')}
        {textInput('tasteNote', '맛 예측 메모')}
      </div>
      <InputArea
        label="한 줄 설명"
        required={false}
        value={value.content.body}
        onValueChange={(body: string) => setContent('body', body)}
      />
      <InputArea
        label="원재료 표기 문구 (복합원재료 괄호 속 성분, 많이 넣은 순 · 원재료 규격서 기준)"
        required={false}
        value={value.content.labelText}
        onValueChange={(labelText: string) => setContent('labelText', labelText)}
      />

      <Checkbox.Group
        legend="문의 때 사용자가 체크해 넘길 항목 (비우면 문의 버튼 없음 · 배합표·원가는 넘기지 않음)"
        value={value.leadItems}
        onValueChange={(leadItems: string[]) => set('leadItems', leadItems as AdLeadItem[])}
        className="flex flex-wrap gap-x-4"
      >
        {AD_LEAD_ITEMS.map((k) => (
          <Checkbox.Item key={k} value={k} label={LEAD_ITEM_LABEL[k]} />
        ))}
      </Checkbox.Group>

      <div className="flex flex-wrap gap-3">
        <Select
          label="과금"
          value={value.billingUnit}
          onValueChange={(v: string | null) => v && set('billingUnit', v as AdBillingUnit)}
          items={items(AD_BILLING_UNITS, (b) => BILLING_LABEL[b])}
        />
        <Input
          label="과금 단가"
          type="number"
          value={String(value.billingAmount)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            set('billingAmount', Number(e.target.value) || 0)
          }
        />
        <Input
          label="월 예산 (고정 슬롯은 안 씀)"
          type="number"
          value={String(value.monthlyBudget)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            set('monthlyBudget', Number(e.target.value) || 0)
          }
        />
        <Input
          label="시작일"
          value={value.startsOn}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('startsOn', e.target.value)}
        />
        <Input
          label="종료일 (비우면 계속)"
          value={value.endsOn ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            set('endsOn', e.target.value.trim() || null)
          }
        />
      </div>

      <Surface className="flex flex-col gap-1 rounded-lg p-4">
        <Text size="sm">
          <b>&lsquo;광고&rsquo; 글자 표시</b> · 항상 켜짐 · 끌 수 없음
        </Text>
        {!parsed.success ? (
          <Text variant="secondary" size="sm">
            필수 칸을 채우면 문구 검사를 해요 (이름·날짜 형식 YYYY-MM-DD).
          </Text>
        ) : problems.length === 0 ? (
          <Text size="sm">문구 검사 통과 · 저장 뒤 [승인하고 게시]를 누를 수 있어요</Text>
        ) : (
          problems.map((p) => (
            <Text key={p.code} size="sm">
              확인 필요 · {problemText(p)}
            </Text>
          ))
        )}
        <Text variant="secondary" size="sm">
          내용을 고치면 다시 검수 대기로 돌아가요. 단가 근거(견적서)·원재료 규격서·원산지 증빙은
          광고주 메모에 받은 날짜와 함께 적어 두세요.
        </Text>
      </Surface>

      <div className="flex gap-2">
        <Button loading={busy} disabled={!parsed.success} onClick={onSave}>
          저장
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          닫기
        </Button>
      </div>
    </Surface>
  )
}

function LeadTable({
  leads,
  campaigns,
  advertiserName,
  busy,
  onExport,
}: {
  leads: AdLeadAdminView[]
  campaigns: AdCampaignAdminView[]
  advertiserName: (id: string) => string
  busy: boolean
  onExport: () => void
}) {
  return (
    <>
      <Text variant="secondary" size="sm">
        사용자가 체크해 동의한 항목만 있어요(배합표·원가 없음). 광고주에게는 계약한 경로로 직접 전해
        주세요 — 앱이 밖으로 보내지 않아요. 철회한 문의는 값이 지워져 있어요.
      </Text>
      <div>
        <Button
          variant="secondary"
          icon={<DownloadSimple />}
          loading={busy}
          disabled={leads.length === 0}
          onClick={onExport}
        >
          살아 있는 문의 CSV 내려받기
        </Button>
      </div>
      <Surface className="overflow-x-auto rounded-lg p-0">
        {leads.length === 0 ? (
          <Empty
            title="문의가 없어요"
            description="광고 카드에서 샘플·견적 문의가 오면 여기에 쌓여요."
          />
        ) : (
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>받은 때</Table.Head>
                <Table.Head>광고주 · 광고</Table.Head>
                <Table.Head>항목</Table.Head>
                <Table.Head>상태</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {leads.map((l) => (
                <Table.Row key={l.id}>
                  <Table.Cell className="whitespace-nowrap text-xs">
                    {fmtTime(l.createdAt)}
                  </Table.Cell>
                  <Table.Cell className="text-xs">
                    <div>{advertiserName(l.advertiserId)}</div>
                    <div className="text-kumo-subtle">
                      {campaigns.find((c) => c.id === l.campaignId)?.content.productName ??
                        l.campaignId}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-xs">
                    {(Object.keys(l.fields) as AdLeadItem[])
                      .map((k) => `${LEAD_ITEM_LABEL[k]}: ${l.fields[k]}`)
                      .join(' · ') || '—'}
                  </Table.Cell>
                  <Table.Cell className="text-xs">
                    {l.status === 'active' ? (
                      <Badge variant="primary">살아 있음</Badge>
                    ) : (
                      `철회 ${fmtTime(l.withdrawnAt)}`
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>
    </>
  )
}
